<template>
  <div
    class="sns-post-item-images-parts"
    :class="{ 'sns-post-item-images-parts--swiper': images.length > 1 }"
    ref="swiper"
  >
    <div v-if="images.length > 1" class="swiper-wrapper">
      <div
        v-for="img in images"
        :key="`${img.src}`"
        class="sns-post-item-images-parts__slider-img swiper-slide"
      >
        <a class="sns-post-item-images-parts__link" :href="postLink" target="_blank">
          <img
            class="sns-post-item-images-parts__img"
            :src="addSignedUrl(img.src)"
            alt=""
            @error="srcError"
          />
        </a>
        <img
          v-if="img.type === 'video'"
          class="sns-post-item-images-parts__img__play-icon"
          :src="require('@/assets/img/icon/video_controller_play.svg')"
          alt=""
        />
      </div>
    </div>

    <a v-else class="sns-post-item-images-parts__link" :href="postLink" target="_blank">
      <img
        class="sns-post-item-images-parts__img"
        :src="addSignedUrl(images[0].src)"
        alt=""
        @error="srcError"
      />
    </a>

    <img
      v-if="images[0].type === 'video' && images.length === 1"
      class="sns-post-item-images-parts__img__play-icon"
      :src="require('@/assets/img/icon/video_controller_play.svg')"
      alt=""
    />

    <div class="swiper-button-prev" v-if="images.length > 1">
      <img :src="require('@/assets/img/icon/icon_swiper_prev.svg')" alt="" />
    </div>
    <div class="swiper-button-next" v-if="images.length > 1">
      <img :src="require('@/assets/img/icon/icon_swiper_next.svg')" alt="" />
    </div>
    <div class="swiper-pagination" v-if="images.length > 1" />
  </div>
</template>

<script lang="ts">
import { defineComponent, PropType } from '@vue/composition-api'
import Swiper, { Navigation, Pagination } from 'swiper'
import 'swiper/swiper-bundle.min.css'
import 'swiper/swiper.min.css'
import { SnsPostImage } from '@/store/stores/pageStore/HomePage/HomePageStore'
import CloudFrontUtil from '@/util/aws/CloudFrontUtil'

/**
 * HOME SNSコンテンツ 投稿リンク タブパーツ
 */
export default defineComponent({
  name: 'SnsPostItemImagesParts',
  props: {
    images: {
      type: Array as PropType<SnsPostImage[]>,
      required: true,
    },
    postLink: {
      type: String,
      required: true,
    },
  },
  setup(props, context) {
    const srcError = (event: Event) => {
      const { target } = event
      if (!(target instanceof HTMLImageElement)) {
        return
      }
      target.closest('.sns-post-item-images-parts')?.remove()
      context.emit('updateSnsContentsData', target.src)
    }
    const addSignedUrl = (imgUrl: string) => CloudFrontUtil.getSignedUrl(imgUrl)

    return {
      srcError,
      addSignedUrl,
    }
  },
  mounted() {
    if (this.images.length > 1) {
      // eslint-disable-next-line
      new Swiper(this.$refs.swiper as HTMLElement, {
        modules: [Navigation, Pagination],
        loop: true,
        spaceBetween: 0,
        navigation: {
          nextEl: '.swiper-button-next',
          prevEl: '.swiper-button-prev',
        },
        pagination: {
          el: '.swiper-pagination',
        },
      })
    }
  },
})
</script>

<style lang="scss" scoped>
.sns-post-item-images-parts {
  position: relative;
  margin-top: min(#{convertPXtoVW(12), 12px});
  margin-bottom: min(#{convertPXtoVW(4), 4px});
  overflow: hidden;
  background: $gray1;

  &__link {
    display: block;
    width: 100%;
    padding-top: 100%;
  }

  &__img {
    position: absolute;
    display: block;
    top: 0;
    left: 0;
    bottom: 0;
    width: 100%;
    height: 100%;
    object-fit: cover;

    &__play-icon {
      position: absolute;
      top: 50%;
      left: 50%;
      width: 20%;
      transform: translate(-50%, -50%);
      z-index: 2;
      opacity: 0.7;
      pointer-events: none;
    }
  }

  .swiper {
    &-slide {
      position: relative;
      display: flex;
      justify-content: center;
      align-items: center;
      width: 100%;
    }

    &-button-prev,
    &-button-next {
      width: auto;
      height: auto;
      margin-top: 0;
      transform: translateY(-50%);

      img {
        width: 24px;
      }

      &::after {
        display: none;
      }

      @media screen and (min-width: $responsiveTabletWidth - 1px) {
        img {
          width: 36px;
        }
      }
    }

    &-button-prev {
      left: 8px;
    }

    &-button-next {
      right: 8px;
    }

    @media screen and (min-width: $responsiveTabletWidth - 1px) {
      &-button-prev {
        left: 8px;
      }

      &-button-next {
        right: 8px;
      }
    }

    &-pagination {
      bottom: 12px;
      display: flex;
      flex-flow: row nowrap;
      align-items: center;
      justify-content: center;
      gap: 5px;
    }
  }

  &.hidden {
    display: none;
  }
}
</style>

<style lang="scss">
.sns-post-item-images-parts {
  .swiper {
    &-pagination {
      &-bullet {
        width: 5px;
        height: 5px;
        margin: 0 !important;
        background: rgba($white, 0.5);
        opacity: 1;

        &-active {
          background: $white;
        }
      }
    }
  }
}
</style>
